import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useToast } from '../contexts/AppContexts';
import {
  AlertTriangle,
  Camera,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Edit,
  FileText,
  GripVertical,
  Package,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Printer,
  Radio,
  Save,
  Search,
  Trash2,
  Upload,
  User,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const getParticipantName = (p) => {
  if (!p) return '';
  return p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unnamed';
};

const ICS204AssignmentList = ({ theme, currentExercise, API_URL }) => {
  const toast = useToast();
  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saveStatus, setSaveStatus] = useState({});
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpandedIds(new Set(assignments.map(a => a.id)));
  const collapseAll = () => setExpandedIds(new Set());
  
  // Form state
  const [formData, setFormData] = useState({
    branch: '',
    division: '',
    group: '',
    stagingArea: '',
    operationalPeriod: '',
    operationalPeriodStart: '',
    operationalPeriodEnd: '',
    operationsPersonnel: {
      operationsSectionChief: '',
      operationsSectionChiefPhone: '',
      branchDirector: '',
      branchDirectorPhone: '',
      divisionGroupSupervisor: '',
      divisionGroupSupervisorPhone: ''
    },
    resources: [],
    workAssignments: '',
    specialInstructions: '',
    communications: {
      radioFrequency: '',
      channelName: '',
      phone: '',
      medicalEmergencyChannel: '',
      additionalEntries: []
    },
    preparedBy: '',
    dateTimePrepared: new Date().toISOString().slice(0, 16)
  });

  const emptyResource = {
    resourceIdentifier: '',
    leader: '',
    contactInfo: '',
    numberOfPersons: '',
    reportingLocation: '',
    reportingTime: '',
    transportation: '',
    notes: '',
    resource_image: ''
  };

  useEffect(() => {
    fetchAssignments();
  }, [currentExercise?.id]);

  const fetchAssignments = async () => {
    if (!currentExercise?.id) {
      setIsLoading(false);
      return;
    }
    
    try {
      const token = secureStorage.getToken();
      const response = await fetch(`${API_URL}/api/assignments?exercise_id=${currentExercise.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssignments(data);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = secureStorage.getToken();
      const payload = {
        ...formData,
        exercise_id: currentExercise?.id,
        id: editingId || `assignment-${Date.now()}`
      };

      const url = editingId 
        ? `${API_URL}/api/assignments/${editingId}`
        : `${API_URL}/api/assignments`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingId ? 'Assignment updated successfully' : 'Assignment created successfully');
        fetchAssignments();
        resetForm();
      } else {
        toast.error('Failed to save assignment');
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('Error saving assignment');
    }
  };

  const handleEdit = (assignment) => {
    // Backward-compat: older records don't have communications.additionalEntries
    // or operationsPersonnel.*Phone fields.
    const normalized = {
      ...assignment,
      operationsPersonnel: {
        operationsSectionChief: '',
        operationsSectionChiefPhone: '',
        branchDirector: '',
        branchDirectorPhone: '',
        divisionGroupSupervisor: '',
        divisionGroupSupervisorPhone: '',
        ...(assignment.operationsPersonnel || {})
      },
      communications: {
        radioFrequency: '',
        channelName: '',
        phone: '',
        medicalEmergencyChannel: '',
        additionalEntries: [],
        ...(assignment.communications || {}),
        additionalEntries: Array.isArray(assignment.communications?.additionalEntries)
          ? assignment.communications.additionalEntries
          : []
      }
    };
    setFormData(normalized);
    setEditingId(assignment.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    
    try {
      const token = secureStorage.getToken();
      const response = await fetch(`${API_URL}/api/assignments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Assignment deleted successfully');
        fetchAssignments();
      } else {
        toast.error('Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Error deleting assignment');
    }
  };

  const resetForm = () => {
    setFormData({
      branch: '',
      division: '',
      group: '',
      stagingArea: '',
      operationalPeriod: '',
      operationalPeriodStart: '',
      operationalPeriodEnd: '',
      operationsPersonnel: {
        operationsSectionChief: '',
        operationsSectionChiefPhone: '',
        branchDirector: '',
        branchDirectorPhone: '',
        divisionGroupSupervisor: '',
        divisionGroupSupervisorPhone: ''
      },
      resources: [],
      workAssignments: '',
      specialInstructions: '',
      communications: {
        radioFrequency: '',
        channelName: '',
        phone: '',
        medicalEmergencyChannel: '',
        additionalEntries: []
      },
      preparedBy: '',
      dateTimePrepared: new Date().toISOString().slice(0, 16)
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const addResource = () => {
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, { ...emptyResource }]
    }));
  };

  const updateResource = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.map((r, i) => 
        i === index ? { ...r, [field]: value } : r
      )
    }));
  };

  const removeResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  // ===== Basic Information: image upload / camera / drag-drop =====
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const [showCameraPreview, setShowCameraPreview] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadingTargetKey, setUploadingTargetKey] = useState(null); // 'resource:<idx>'
  const [cameraTarget, setCameraTarget] = useState(null); // same shape as uploadingTargetKey

  // Resize/compress to balance viewing quality with upload speed.
  // 1600px longest edge keeps text/photos sharp on screens up to ~1920px wide
  // while JPEG quality 0.82 brings most camera captures from 3-6 MB down to
  // ~150-400 KB — fast upload even on cellular.
  const resizeImage = useCallback((file) => new Promise((resolve, reject) => {
    const MAX_EDGE = 1600;
    const QUALITY = 0.82;
    // Skip resize for tiny GIFs/PNG icons that are already smaller than the cap
    if (file.size < 200 * 1024 && file.type !== 'image/jpeg') {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; // flatten transparency against white for JPEG
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            resolve(file); // fall back to original if encode fails
            return;
          }
          // If the "resized" blob is bigger than the original (rare for
          // already-optimized PNGs), keep the original.
          if (blob.size >= file.size && scale === 1) {
            resolve(file);
            return;
          }
          const stem = (file.name || 'image').replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${stem}.jpg`, { type: 'image/jpeg' }));
        }, 'image/jpeg', QUALITY);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file); // fall back to original if decode fails
    };
    img.src = url;
  }), []);

  const uploadImageFile = useCallback(async (file, target = 'basicInfo') => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_BYTES) {
      toast.error('Image is larger than 10 MB — please choose a smaller file');
      return;
    }
    const targetKey = typeof target === 'string'
      ? target
      : (target?.type === 'resource' ? `resource:${target.index}` : 'basicInfo');
    try {
      setIsUploadingImage(true);
      setUploadingTargetKey(targetKey);
      const resized = await resizeImage(file).catch(() => file);
      const token = secureStorage.getToken();
      const fd = new FormData();
      fd.append('file', resized);
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const body = await res.json();
      const url = body.file_path || body.url;
      if (!url) throw new Error('Upload did not return a file path');
      if (target?.type === 'resource') {
        const idx = target.index;
        setFormData(prev => ({
          ...prev,
          resources: prev.resources.map((r, i) => (i === idx ? { ...r, resource_image: url } : r))
        }));
      }
      toast.success('Image uploaded');
    } catch (err) {
      console.error('ICS 204 image upload failed:', err);
      toast.error(err?.message || 'Image upload failed');
    } finally {
      setIsUploadingImage(false);
      setUploadingTargetKey(null);
    }
  }, [API_URL, toast, resizeImage]);

  const handleResourceFilePick = (index, e) => {
    const file = e.target.files?.[0];
    if (file) uploadImageFile(file, { type: 'resource', index });
    if (e.target) e.target.value = '';
  };

  const openCamera = async (target = 'basicInfo') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      cameraStreamRef.current = stream;
      setCameraTarget(target);
      setShowCameraPreview(true);
      setTimeout(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          cameraVideoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (err) {
      console.error('Camera access failed:', err);
      toast.error('Could not access camera. Please check browser permissions.');
    }
  };

  const stopCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    setShowCameraPreview(false);
    setCameraTarget(null);
  }, []);

  const capturePhoto = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) {
      toast.error('Camera is not ready yet, try again in a moment');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const activeTarget = cameraTarget || 'basicInfo';
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error('Capture failed');
        return;
      }
      const file = new File([blob], `ics204-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopCamera();
      uploadImageFile(file, activeTarget);
    }, 'image/jpeg', 0.92);
  };

  const removeResourceImage = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.map((r, i) => (i === index ? { ...r, resource_image: '' } : r))
    }));
  };

  // Per-row drag-and-drop handlers for Resource photos
  const [draggingResourceIndex, setDraggingResourceIndex] = useState(null);
  const makeResourceDropHandlers = (index) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    },
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDraggingResourceIndex(index);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only clear highlight when leaving the outer wrapper, not when
      // moving between inner children.
      if (e.currentTarget === e.target) {
        setDraggingResourceIndex(prev => (prev === index ? null : prev));
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDraggingResourceIndex(null);
      const file = e.dataTransfer?.files?.[0];
      if (file) uploadImageFile(file, { type: 'resource', index });
    }
  });

  // Stop camera on unmount
  useEffect(() => () => stopCamera(), [stopCamera]);

  // ===== Communications: additional entries =====
  const emptyCommEntry = {
    radioFrequency: '',
    channelName: '',
    phone: '',
    medicalEmergencyChannel: ''
  };

  const addCommEntry = () => {
    setFormData(prev => ({
      ...prev,
      communications: {
        ...prev.communications,
        additionalEntries: [
          ...(prev.communications?.additionalEntries || []),
          { ...emptyCommEntry }
        ]
      }
    }));
  };

  const updateCommEntry = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      communications: {
        ...prev.communications,
        additionalEntries: (prev.communications?.additionalEntries || []).map(
          (entry, i) => (i === index ? { ...entry, [field]: value } : entry)
        )
      }
    }));
  };

  const removeCommEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      communications: {
        ...prev.communications,
        additionalEntries: (prev.communications?.additionalEntries || []).filter(
          (_, i) => i !== index
        )
      }
    }));
  };

  // ===== Quick Assign =====
  const [participants, setParticipants] = useState([]);
  const [unitsList, setUnitsList] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('resources');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropTargetRow, setDropTargetRow] = useState(null); // null | 'section' | rowIndex
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMsg({ message, type });
    toastTimer.current = setTimeout(() => setToastMsg(null), 3000);
  }, []);

  useEffect(() => {
    const fetchPersonnelAndResources = async () => {
      if (!currentExercise?.id) return;
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const pRes = await axios.get(`${API_URL}/api/exercises/${currentExercise.id}/participants`, { headers });
        setParticipants(pRes.data || []);
      } catch (err) {
        console.warn('[ICS204] participants fetch failed:', err?.message || err);
      }
      try {
        const rRes = await axios.get(`${API_URL}/api/resources?exercise_id=${currentExercise.id}`, { headers });
        const allUnits = [];
        for (const res of (rRes.data || [])) {
          try {
            const u = await axios.get(`${API_URL}/api/resources/${res.id}/units`, { headers });
            (u.data || []).forEach(unit => allUnits.push({
              ...unit,
              resource_type: res.resource_type,
              resource_image: res.resource_image || '',
              parent_resource_id: res.id
            }));
          } catch (unitErr) {
            console.warn(`[ICS204] units fetch failed for resource ${res.id}:`, unitErr?.message || unitErr);
          }
        }
        setUnitsList(allUnits);
      } catch (err) {
        console.warn('[ICS204] resources fetch failed:', err?.message || err);
      }
    };
    fetchPersonnelAndResources();
  }, [currentExercise?.id, API_URL]);

  const onDragStart = (e, item, itemType) => {
    e.dataTransfer.setData('item_type', itemType);
    if (itemType === 'resource') {
      e.dataTransfer.setData('resource_unit_id', item.id || '');
      e.dataTransfer.setData('resource_unit_name', item.unit_name || '');
      e.dataTransfer.setData('resource_personnel', String(item.personnel_needed || ''));
      e.dataTransfer.setData('resource_location', item.location || '');
      e.dataTransfer.setData('resource_type', item.resource_type || '');
      e.dataTransfer.setData('resource_image', item.resource_image || '');
      e.dataTransfer.setData('resource_label', `${item.unit_name || 'Unit'}${item.resource_type ? ` (${item.resource_type})` : ''}`);
    } else {
      e.dataTransfer.setData('personnel_name', getParticipantName(item));
      e.dataTransfer.setData('personnel_phone', item.phone || item.contact || '');
    }
    e.dataTransfer.effectAllowed = 'copy';
  };

  const onDragOverSection = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetRow('section');
  };

  const onDragOverRow = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropTargetRow(idx);
  };

  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetRow(null);
  };

  const onDropOnSection = (e) => {
    e.preventDefault();
    setDropTargetRow(null);
    const itemType = e.dataTransfer.getData('item_type');
    if (itemType === 'resource') {
      const unitId = e.dataTransfer.getData('resource_unit_id');
      const unitName = e.dataTransfer.getData('resource_unit_name');
      const personnel = e.dataTransfer.getData('resource_personnel');
      const location = e.dataTransfer.getData('resource_location');
      const resType = e.dataTransfer.getData('resource_type');
      const resImage = e.dataTransfer.getData('resource_image');
      const label = e.dataTransfer.getData('resource_label');
      const newRow = {
        ...emptyResource,
        // Compact view metadata
        quickAssign: true,
        unit_id: unitId,
        unit_name: unitName,
        resource_type: resType,
        resource_image: resImage,
        // Standard ICS 204 fields (still saved for the form's data model)
        resourceIdentifier: unitName,
        numberOfPersons: personnel || '',
        reportingLocation: location || ''
      };
      setFormData(prev => ({ ...prev, resources: [...prev.resources, newRow] }));
      showToast(`Added resource "${label}" to assignments`, 'success');
    } else if (itemType === 'personnel') {
      const name = e.dataTransfer.getData('personnel_name');
      const phone = e.dataTransfer.getData('personnel_phone');
      const newRow = { ...emptyResource, leader: name, contactInfo: phone || '' };
      setFormData(prev => ({ ...prev, resources: [...prev.resources, newRow] }));
      showToast(`Added new resource row with leader "${name}"`, 'success');
    }
  };

  const onDropOnRow = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetRow(null);
    const itemType = e.dataTransfer.getData('item_type');
    if (itemType === 'personnel') {
      const name = e.dataTransfer.getData('personnel_name');
      const phone = e.dataTransfer.getData('personnel_phone');
      setFormData(prev => ({
        ...prev,
        resources: prev.resources.map((r, i) =>
          i === idx ? { ...r, leader: name, contactInfo: r.contactInfo || phone || '' } : r
        )
      }));
      showToast(`Set "${name}" as leader of Resource #${idx + 1}`, 'success');
    } else if (itemType === 'resource') {
      const unitId = e.dataTransfer.getData('resource_unit_id');
      const unitName = e.dataTransfer.getData('resource_unit_name');
      const personnel = e.dataTransfer.getData('resource_personnel');
      const location = e.dataTransfer.getData('resource_location');
      const resType = e.dataTransfer.getData('resource_type');
      const resImage = e.dataTransfer.getData('resource_image');
      const label = e.dataTransfer.getData('resource_label');
      setFormData(prev => ({
        ...prev,
        resources: prev.resources.map((r, i) =>
          i === idx
            ? {
                ...r,
                quickAssign: true,
                unit_id: unitId,
                unit_name: unitName,
                resource_type: resType,
                resource_image: resImage,
                resourceIdentifier: unitName || r.resourceIdentifier,
                numberOfPersons: r.numberOfPersons || personnel || '',
                reportingLocation: r.reportingLocation || location || ''
              }
            : r
        )
      }));
      showToast(`Updated Resource #${idx + 1} from "${label}"`, 'success');
    }
  };

  const filteredParticipants = participants.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return getParticipantName(p).toLowerCase().includes(q)
      || (p.position || p.role || '').toLowerCase().includes(q);
  });

  const filteredUnits = unitsList.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (r.unit_name || '').toLowerCase().includes(q)
      || (r.resource_type || '').toLowerCase().includes(q);
  });

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ICS 204 - Assignment List</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; border-bottom: 2px solid black; padding-bottom: 10px; }
          .header-info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
          .section { margin-bottom: 20px; padding: 10px; border: 1px solid #ccc; }
          .section-title { font-weight: bold; margin-bottom: 10px; background: #f0f0f0; padding: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
          th { background: #f0f0f0; }
          .label { font-weight: bold; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>ICS 204 - ASSIGNMENT LIST</h1>
        <div class="header-info">
          <div><span class="label">Incident Name:</span> ${currentExercise?.exercise_name || ''}</div>
          <div><span class="label">Operational Period:</span> ${formData.operationalPeriod}</div>
          <div><span class="label">Branch:</span> ${formData.branch}</div>
          <div><span class="label">Division/Group:</span> ${formData.division || formData.group}</div>
        </div>
        
        <div class="section">
          <div class="section-title">OPERATIONS PERSONNEL</div>
          <div><span class="label">Operations Section Chief:</span> ${formData.operationsPersonnel.operationsSectionChief}${formData.operationsPersonnel.operationsSectionChiefPhone ? ` &nbsp;&nbsp; <span class="label">Phone:</span> ${formData.operationsPersonnel.operationsSectionChiefPhone}` : ''}</div>
          <div><span class="label">Branch Director:</span> ${formData.operationsPersonnel.branchDirector}${formData.operationsPersonnel.branchDirectorPhone ? ` &nbsp;&nbsp; <span class="label">Phone:</span> ${formData.operationsPersonnel.branchDirectorPhone}` : ''}</div>
          <div><span class="label">Division/Group Supervisor:</span> ${formData.operationsPersonnel.divisionGroupSupervisor}${formData.operationsPersonnel.divisionGroupSupervisorPhone ? ` &nbsp;&nbsp; <span class="label">Phone:</span> ${formData.operationsPersonnel.divisionGroupSupervisorPhone}` : ''}</div>
        </div>
        
        <div class="section">
          <div class="section-title">RESOURCES ASSIGNED</div>
          <table>
            <thead>
              <tr>
                <th>Resource Identifier</th>
                <th>Leader/Contact</th>
                <th>Contact Info</th>
                <th># of Persons</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${formData.resources.map(r => `
                <tr>
                  <td>${r.resourceIdentifier || ''}</td>
                  <td>${r.leader || ''}</td>
                  <td>${r.contactInfo || ''}</td>
                  <td>${r.numberOfPersons || ''}</td>
                  <td>${r.notes || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        
        <div class="section">
          <div class="section-title">WORK ASSIGNMENTS</div>
          <div>${formData.workAssignments}</div>
        </div>
        
        <div class="section">
          <div class="section-title">SPECIAL INSTRUCTIONS</div>
          <div>${formData.specialInstructions}</div>
        </div>
        
        <div class="section">
          <div class="section-title">COMMUNICATIONS</div>
          <div><span class="label">Radio Frequency:</span> ${formData.communications.radioFrequency}</div>
          <div><span class="label">Channel Name:</span> ${formData.communications.channelName}</div>
          <div><span class="label">Phone:</span> ${formData.communications.phone}</div>
          <div><span class="label">Medical Emergency Channel:</span> ${formData.communications.medicalEmergencyChannel}</div>
          ${(formData.communications.additionalEntries || []).map((entry, idx) => `
            <div style="margin-top: 10px; padding-top: 8px; border-top: 1px dashed #999;">
              <div style="font-weight: bold; margin-bottom: 4px;">Additional Entry ${idx + 1}</div>
              <div><span class="label">Radio Frequency:</span> ${entry.radioFrequency || ''}</div>
              <div><span class="label">Channel Name:</span> ${entry.channelName || ''}</div>
              <div><span class="label">Phone:</span> ${entry.phone || ''}</div>
              <div><span class="label">Medical Emergency Channel:</span> ${entry.medicalEmergencyChannel || ''}</div>
            </div>
          `).join('')}
        </div>
        
        <div style="margin-top: 20px;">
          <div><span class="label">Prepared By:</span> ${formData.preparedBy}</div>
          <div><span class="label">Date/Time:</span> ${formData.dateTimePrepared}</div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 h-full overflow-auto max-h-[calc(100vh-120px)] transition-all duration-300 ${sidebarOpen && showAddForm ? 'pr-[21rem]' : ''}`}>
      {/* Toast for Quick Assign */}
      {toastMsg && (
        <div
          className={`fixed top-4 z-50 px-4 py-3 rounded-lg shadow-lg border backdrop-blur-sm transition-all ${
            sidebarOpen ? 'right-[22rem]' : 'right-4'
          } ${
            toastMsg.type === 'error'
              ? 'bg-red-900/90 border-red-700 text-red-100'
              : 'bg-green-900/90 border-green-700 text-green-100'
          }`}
          data-testid="ics204-quick-assign-toast"
        >
          <div className="flex items-center gap-2">
            {toastMsg.type === 'error' ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            <span className="text-sm font-medium">{toastMsg.message}</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-orange-500 flex items-center gap-2">
            <ClipboardList className="w-6 h-6" />
            ICS 204 - Assignment List
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Division/Group Assignment List for Operational Period
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {showAddForm && (
            <>
              <Button
                variant="outline"
                onClick={() => setSidebarOpen(o => !o)}
                className={`border-blue-500/40 text-blue-400 hover:bg-blue-500/10 ${sidebarOpen ? 'bg-blue-500/10' : ''}`}
                data-testid="ics204-toggle-quick-assign-btn"
              >
                {sidebarOpen ? <PanelRightClose className="w-4 h-4 mr-2" /> : <PanelRightOpen className="w-4 h-4 mr-2" />}
                Quick Assign
              </Button>
              <Button
                variant="outline"
                onClick={handlePrint}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print ICS 204
              </Button>
            </>
          )}
          <Button
            onClick={() => showAddForm ? resetForm() : setShowAddForm(true)}
            className={showAddForm ? "bg-gray-600 hover:bg-gray-700" : "bg-orange-500 hover:bg-orange-600 text-black"}
          >
            {showAddForm ? (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                New Assignment
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className={`${theme.colors.card} border ${theme.colors.border}`}>
          <CardContent className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Basic Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white">Branch</Label>
                  <Input
                    value={formData.branch}
                    onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                    placeholder="Enter branch name"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Division</Label>
                  <Input
                    value={formData.division}
                    onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                    placeholder="Enter division"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Group</Label>
                  <Input
                    value={formData.group}
                    onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                    placeholder="Enter group"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
              </div>

              {/* Reference Image block moved above (top-left corner of Basic Information) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-white">Staging Area</Label>
                  <Input
                    value={formData.stagingArea}
                    onChange={(e) => setFormData({ ...formData, stagingArea: e.target.value })}
                    placeholder="Enter staging area"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Operational Period</Label>
                  <Input
                    value={formData.operationalPeriod}
                    onChange={(e) => setFormData({ ...formData, operationalPeriod: e.target.value })}
                    placeholder="e.g., #1, Day Shift"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-white text-xs">Period Start</Label>
                    <Input
                      type="datetime-local"
                      value={formData.operationalPeriodStart}
                      onChange={(e) => setFormData({ ...formData, operationalPeriodStart: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Period End</Label>
                    <Input
                      type="datetime-local"
                      value={formData.operationalPeriodEnd}
                      onChange={(e) => setFormData({ ...formData, operationalPeriodEnd: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Operations Personnel */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Operations Personnel
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Operations Section Chief</Label>
                  <Input
                    value={formData.operationsPersonnel.operationsSectionChief}
                    onChange={(e) => setFormData({
                      ...formData,
                      operationsPersonnel: { ...formData.operationsPersonnel, operationsSectionChief: e.target.value }
                    })}
                    placeholder="Name"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-ops-chief-name"
                  />
                </div>
                <div>
                  <Label className="text-white">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.operationsPersonnel.operationsSectionChiefPhone || ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 0) {
                        if (value.length <= 3) {
                          value = value;
                        } else if (value.length <= 6) {
                          value = value.slice(0, 3) + '-' + value.slice(3);
                        } else {
                          value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                        }
                      }
                      setFormData({
                        ...formData,
                        operationsPersonnel: { ...formData.operationsPersonnel, operationsSectionChiefPhone: value }
                      });
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && !/^\d{3}-\d{3}-\d{4}$/.test(value)) {
                        toast.error('Phone must be a valid 10-digit number (123-456-7890)');
                      }
                    }}
                    placeholder="123-456-7890"
                    maxLength={12}
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-ops-chief-phone"
                  />
                </div>
                <div>
                  <Label className="text-white">Branch Director</Label>
                  <Input
                    value={formData.operationsPersonnel.branchDirector}
                    onChange={(e) => setFormData({
                      ...formData,
                      operationsPersonnel: { ...formData.operationsPersonnel, branchDirector: e.target.value }
                    })}
                    placeholder="Name"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-branch-director-name"
                  />
                </div>
                <div>
                  <Label className="text-white">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.operationsPersonnel.branchDirectorPhone || ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 0) {
                        if (value.length <= 3) {
                          value = value;
                        } else if (value.length <= 6) {
                          value = value.slice(0, 3) + '-' + value.slice(3);
                        } else {
                          value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                        }
                      }
                      setFormData({
                        ...formData,
                        operationsPersonnel: { ...formData.operationsPersonnel, branchDirectorPhone: value }
                      });
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && !/^\d{3}-\d{3}-\d{4}$/.test(value)) {
                        toast.error('Phone must be a valid 10-digit number (123-456-7890)');
                      }
                    }}
                    placeholder="123-456-7890"
                    maxLength={12}
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-branch-director-phone"
                  />
                </div>
                <div>
                  <Label className="text-white">Division/Group Supervisor</Label>
                  <Input
                    value={formData.operationsPersonnel.divisionGroupSupervisor}
                    onChange={(e) => setFormData({
                      ...formData,
                      operationsPersonnel: { ...formData.operationsPersonnel, divisionGroupSupervisor: e.target.value }
                    })}
                    placeholder="Name"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-div-group-sup-name"
                  />
                </div>
                <div>
                  <Label className="text-white">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.operationsPersonnel.divisionGroupSupervisorPhone || ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 0) {
                        if (value.length <= 3) {
                          value = value;
                        } else if (value.length <= 6) {
                          value = value.slice(0, 3) + '-' + value.slice(3);
                        } else {
                          value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                        }
                      }
                      setFormData({
                        ...formData,
                        operationsPersonnel: { ...formData.operationsPersonnel, divisionGroupSupervisorPhone: value }
                      });
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && !/^\d{3}-\d{3}-\d{4}$/.test(value)) {
                        toast.error('Phone must be a valid 10-digit number (123-456-7890)');
                      }
                    }}
                    placeholder="123-456-7890"
                    maxLength={12}
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-div-group-sup-phone"
                  />
                </div>
              </div>
            </div>

            {/* Resources Assigned */}
            <div
              className={`space-y-4 p-3 -m-3 rounded-lg transition-all ${
                dropTargetRow === 'section' ? 'ring-2 ring-blue-500/40 bg-blue-500/5' : ''
              }`}
              onDragOver={onDragOverSection}
              onDragLeave={onDragLeave}
              onDrop={onDropOnSection}
              data-testid="ics204-resources-assigned-section"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Resources Assigned
                </h3>
                <Button
                  onClick={addResource}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Resource
                </Button>
              </div>
              {sidebarOpen && dropTargetRow === 'section' && (
                <div className="border-2 border-dashed border-blue-500/60 rounded-lg p-3 bg-blue-500/5 text-center">
                  <p className="text-blue-400 text-xs font-medium">Drop here to add a new resource row</p>
                </div>
              )}
              
              {formData.resources.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-gray-600 rounded-lg">
                  <Package className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">No resources assigned yet</p>
                  <Button
                    onClick={addResource}
                    size="sm"
                    variant="link"
                    className="text-orange-400 hover:text-orange-300 mt-2"
                  >
                    Add your first resource
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.resources.map((resource, index) => (
                    <Card
                      key={resource.id || `resource-${index}`}
                      className={`bg-gray-800/50 border-gray-700 transition-all ${
                        dropTargetRow === index ? 'ring-2 ring-blue-500/50 border-blue-500/50' : ''
                      }`}
                      onDragOver={(e) => onDragOverRow(e, index)}
                      onDragLeave={onDragLeave}
                      onDrop={(e) => onDropOnRow(e, index)}
                      data-testid={`ics204-resource-row-${index}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <span className="text-sm font-medium text-orange-400">Resource #{index + 1}</span>
                          <Button
                            onClick={() => removeResource(index)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-7 w-7 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {resource.quickAssign ? (
                          <div
                            className="grid grid-cols-12 gap-3 items-center bg-gray-900/40 border border-gray-700/60 rounded-lg p-3"
                            data-testid={`ics204-quickassign-row-${index}`}
                          >
                            {/* Image */}
                            <div className="col-span-3 md:col-span-2">
                              {resource.resource_image ? (
                                <img
                                  src={resource.resource_image}
                                  alt={resource.unit_name || 'Resource'}
                                  className="w-full h-16 object-cover rounded-md border border-gray-700"
                                />
                              ) : (
                                <div className="w-full h-16 flex items-center justify-center bg-gray-800 rounded-md border border-gray-700">
                                  <Package className="w-6 h-6 text-gray-600" />
                                </div>
                              )}
                            </div>
                            {/* Resource Type */}
                            <div className="col-span-9 md:col-span-4">
                              <Label className="text-gray-400 text-xs">Resource Type</Label>
                              <p className="text-white text-sm font-medium truncate">{resource.resource_type || '—'}</p>
                            </div>
                            {/* Unit Name / ID */}
                            <div className="col-span-7 md:col-span-4">
                              <Label className="text-gray-400 text-xs">Unit Name / ID</Label>
                              <p className="text-white text-sm font-medium truncate">{resource.unit_name || resource.resourceIdentifier || '—'}</p>
                            </div>
                            {/* # of Personnel */}
                            <div className="col-span-5 md:col-span-2">
                              <Label className="text-gray-400 text-xs"># of Personnel</Label>
                              <p className="text-white text-sm font-medium">{resource.numberOfPersons || '0'}</p>
                            </div>
                          </div>
                        ) : (
                        <div className="flex gap-3">
                          {/* Photo cell — spans both rows (left side) */}
                          <div
                            {...makeResourceDropHandlers(index)}
                            className={`shrink-0 self-stretch ${draggingResourceIndex === index ? 'ring-2 ring-orange-400 ring-offset-1 ring-offset-gray-800 rounded' : ''}`}
                            data-testid={`ics204-resource-image-cell-${index}`}
                          >
                            <Label className="text-gray-400 text-xs">Photo</Label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleResourceFilePick(index, e)}
                              className="hidden"
                              id={`ics204-resource-image-input-${index}`}
                              data-testid={`ics204-resource-image-input-${index}`}
                            />
                            {resource.resource_image ? (
                              <div className="relative h-24 w-24" data-testid={`ics204-resource-image-preview-wrap-${index}`}>
                                <img
                                  src={resource.resource_image.startsWith('http') ? resource.resource_image : `${API_URL}${resource.resource_image}`}
                                  alt={`Resource ${index + 1}`}
                                  onClick={() => document.getElementById(`ics204-resource-image-input-${index}`)?.click()}
                                  className="h-24 w-24 rounded border border-gray-600 object-cover cursor-pointer hover:border-orange-400"
                                  title="Click to replace · drag an image here to upload"
                                  data-testid={`ics204-resource-image-preview-${index}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeResourceImage(index)}
                                  className="absolute -top-1.5 -right-1.5 bg-red-600 hover:bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs leading-none"
                                  aria-label="Remove photo"
                                  data-testid={`ics204-resource-image-remove-${index}`}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <div
                                className={`h-24 w-24 rounded border border-dashed flex flex-col items-center justify-center gap-1 ${
                                  draggingResourceIndex === index
                                    ? 'border-orange-400 bg-orange-500/10'
                                    : 'border-gray-600 bg-gray-700/50'
                                }`}
                                title="Drag an image here, or use the buttons"
                              >
                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => document.getElementById(`ics204-resource-image-input-${index}`)?.click()}
                                    disabled={uploadingTargetKey === `resource:${index}`}
                                    className="h-8 w-8 rounded hover:bg-gray-600 text-gray-300 flex items-center justify-center disabled:opacity-50"
                                    title="Upload photo"
                                    data-testid={`ics204-resource-image-upload-btn-${index}`}
                                  >
                                    <Upload className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openCamera({ type: 'resource', index })}
                                    disabled={uploadingTargetKey === `resource:${index}`}
                                    className="h-8 w-8 rounded hover:bg-gray-600 text-gray-300 flex items-center justify-center disabled:opacity-50"
                                    title="Take photo"
                                    data-testid={`ics204-resource-image-camera-btn-${index}`}
                                  >
                                    <Camera className="w-4 h-4" />
                                  </button>
                                </div>
                                <p className="text-[10px] text-gray-500 text-center leading-tight px-1">Drag image<br />or tap</p>
                              </div>
                            )}
                          </div>

                          {/* Right side — 2 stacked rows */}
                          <div className="flex-1 min-w-0 space-y-3">
                            {/* Row 1: 4 text fields */}
                            <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_2fr_1fr] gap-3 md:items-end">
                              <div>
                                <Label className="text-gray-400 text-xs">Resource Identifier</Label>
                                <Input
                                  value={resource.resourceIdentifier}
                                  onChange={(e) => updateResource(index, 'resourceIdentifier', e.target.value)}
                                  placeholder="e.g., E-101, T-5"
                                  className="bg-gray-700 border-gray-600 text-white text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-400 text-xs">Leader/Contact</Label>
                                <Input
                                  value={resource.leader}
                                  onChange={(e) => updateResource(index, 'leader', e.target.value)}
                                  placeholder="Leader name"
                                  className="bg-gray-700 border-gray-600 text-white text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-400 text-xs">Contact Info</Label>
                                <Input
                                  value={resource.contactInfo}
                                  onChange={(e) => updateResource(index, 'contactInfo', e.target.value)}
                                  placeholder="Name and phone"
                                  className="bg-gray-700 border-gray-600 text-white text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-gray-400 text-xs"># of Persons</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={resource.numberOfPersons}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || /^\d+$/.test(value)) {
                                      updateResource(index, 'numberOfPersons', value);
                                    } else {
                                      toast.error('# of Persons must be a valid number');
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value !== '' && !/^\d+$/.test(value)) {
                                      toast.error('# of Persons must be a valid number');
                                      updateResource(index, 'numberOfPersons', '');
                                    }
                                  }}
                                  placeholder="0"
                                  className="bg-gray-700 border-gray-600 text-white text-sm"
                                />
                              </div>
                            </div>

                            {/* Row 2: Notes full width */}
                            <div>
                              <Label className="text-gray-400 text-xs">Notes</Label>
                              <Input
                                value={resource.notes}
                                onChange={(e) => updateResource(index, 'notes', e.target.value)}
                                placeholder="Additional info"
                                className="bg-gray-700 border-gray-600 text-white text-sm"
                              />
                            </div>
                          </div>
                        </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Work Assignments */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Work Assignments
              </h3>
              <Textarea
                value={formData.workAssignments}
                onChange={(e) => setFormData({ ...formData, workAssignments: e.target.value })}
                placeholder="Describe work assignments, tasks, and objectives for this division/group..."
                className="bg-gray-800 border-gray-600 text-white min-h-[100px]"
              />
            </div>

            {/* Special Instructions */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Special Instructions
              </h3>
              <Textarea
                value={formData.specialInstructions}
                onChange={(e) => setFormData({ ...formData, specialInstructions: e.target.value })}
                placeholder="Enter any special instructions, safety considerations, or important notes..."
                className="bg-gray-800 border-gray-600 text-white min-h-[100px]"
              />
            </div>

            {/* Communications */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                  <Radio className="w-5 h-5" />
                  Communications
                </h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addCommEntry}
                  className="border-orange-500/60 text-orange-400 hover:bg-orange-500/10"
                  data-testid="ics204-add-comm-entry-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Entry
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-white">Radio Frequency</Label>
                  <Input
                    type="number"
                    step="0.001"
                    value={formData.communications.radioFrequency}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d{0,3}$/.test(value)) {
                        setFormData({
                          ...formData,
                          communications: { ...formData.communications, radioFrequency: value }
                        });
                      } else {
                        toast.error('Radio Frequency must be a valid number (up to 3 decimal places)');
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && !/^\d+(\.\d{1,3})?$/.test(value)) {
                        toast.error('Radio Frequency must be a valid number (up to 3 decimal places)');
                      }
                    }}
                    placeholder="e.g., 154.280"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-comm-radio-freq-primary"
                  />
                </div>
                <div>
                  <Label className="text-white">Channel Name</Label>
                  <Input
                    value={formData.communications.channelName}
                    onChange={(e) => setFormData({
                      ...formData,
                      communications: { ...formData.communications, channelName: e.target.value }
                    })}
                    placeholder="e.g., Command"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-comm-channel-primary"
                  />
                </div>
                <div>
                  <Label className="text-white">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.communications.phone}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                      // Auto-format as 123-456-7890
                      if (value.length > 0) {
                        if (value.length <= 3) {
                          value = value;
                        } else if (value.length <= 6) {
                          value = value.slice(0, 3) + '-' + value.slice(3);
                        } else {
                          value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                        }
                      }
                      setFormData({
                        ...formData,
                        communications: { ...formData.communications, phone: value }
                      });
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && !/^\d{3}-\d{3}-\d{4}$/.test(value)) {
                        toast.error('Phone must be a valid 10-digit number (123-456-7890)');
                      }
                    }}
                    placeholder="123-456-7890"
                    maxLength={12}
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-comm-phone-primary"
                  />
                </div>
                <div>
                  <Label className="text-white">Medical Emergency Channel</Label>
                  <Input
                    value={formData.communications.medicalEmergencyChannel}
                    onChange={(e) => setFormData({
                      ...formData,
                      communications: { ...formData.communications, medicalEmergencyChannel: e.target.value }
                    })}
                    placeholder="Emergency channel"
                    className="bg-gray-800 border-gray-600 text-white"
                    data-testid="ics204-comm-med-primary"
                  />
                </div>
              </div>

              {/* Additional communications entries */}
              {(formData.communications.additionalEntries || []).map((entry, idx) => (
                <div
                  key={idx}
                  className="relative grid grid-cols-1 md:grid-cols-4 gap-4 p-3 rounded-md border border-gray-700/60 bg-gray-800/40"
                  data-testid={`ics204-comm-entry-${idx}`}
                >
                  <div className="md:col-span-4 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-400">
                      Additional Entry {idx + 1}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeCommEntry(idx)}
                      className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`ics204-remove-comm-entry-${idx}`}
                      aria-label={`Remove communications entry ${idx + 1}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div>
                    <Label className="text-white">Radio Frequency</Label>
                    <Input
                      type="number"
                      step="0.001"
                      value={entry.radioFrequency}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d{0,3}$/.test(value)) {
                          updateCommEntry(idx, 'radioFrequency', value);
                        } else {
                          toast.error('Radio Frequency must be a valid number (up to 3 decimal places)');
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value !== '' && !/^\d+(\.\d{1,3})?$/.test(value)) {
                          toast.error('Radio Frequency must be a valid number (up to 3 decimal places)');
                        }
                      }}
                      placeholder="e.g., 154.280"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Channel Name</Label>
                    <Input
                      value={entry.channelName}
                      onChange={(e) => updateCommEntry(idx, 'channelName', e.target.value)}
                      placeholder="e.g., Tactical 2"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Phone</Label>
                    <Input
                      type="tel"
                      value={entry.phone}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length > 0) {
                          if (value.length <= 3) {
                            value = value;
                          } else if (value.length <= 6) {
                            value = value.slice(0, 3) + '-' + value.slice(3);
                          } else {
                            value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                          }
                        }
                        updateCommEntry(idx, 'phone', value);
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value !== '' && !/^\d{3}-\d{3}-\d{4}$/.test(value)) {
                          toast.error('Phone must be a valid 10-digit number (123-456-7890)');
                        }
                      }}
                      placeholder="123-456-7890"
                      maxLength={12}
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Medical Emergency Channel</Label>
                    <Input
                      value={entry.medicalEmergencyChannel}
                      onChange={(e) => updateCommEntry(idx, 'medicalEmergencyChannel', e.target.value)}
                      placeholder="Emergency channel"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Prepared By */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-orange-500 flex items-center gap-2">
                <User className="w-5 h-5" />
                Prepared By
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Name</Label>
                  <Input
                    value={formData.preparedBy}
                    onChange={(e) => setFormData({ ...formData, preparedBy: e.target.value })}
                    placeholder="Your name"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Date/Time Prepared</Label>
                  <Input
                    type="datetime-local"
                    value={formData.dateTimePrepared}
                    onChange={(e) => setFormData({ ...formData, dateTimePrepared: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
              <Button
                variant="outline"
                onClick={resetForm}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-orange-500 hover:bg-orange-600 text-black"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update Assignment' : 'Save Assignment'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Camera Preview Modal */}
      {showCameraPreview && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
          style={{ zIndex: 10050 }}
          data-testid="ics204-camera-preview-modal"
        >
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Capture Reference Image
              </h3>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={stopCamera}
                className="text-gray-400 hover:text-white"
                aria-label="Close camera"
                data-testid="ics204-camera-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <video
              ref={cameraVideoRef}
              playsInline
              muted
              className="w-full max-h-[60vh] bg-black rounded-md"
            />
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={stopCamera}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
                data-testid="ics204-camera-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={capturePhoto}
                className="bg-orange-500 hover:bg-orange-600 text-black"
                data-testid="ics204-camera-capture-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Capture
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assignments List */}
      {!showAddForm && (
        <>
          {assignments.length === 0 ? (
            <Card className={`${theme.colors.card} border ${theme.colors.border}`}>
              <CardContent className="p-12 text-center">
                <ClipboardList className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Assignment Lists</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Create your first ICS 204 Assignment List to track division/group assignments, 
                  resources, and work tasks for this operational period.
                </p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-black"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Assignment
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Toolbar: Expand All / Collapse All */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">
                  {assignments.length} assignment{assignments.length === 1 ? '' : 's'}
                  {expandedIds.size > 0 && ` · ${expandedIds.size} expanded`}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={expandAll}
                    disabled={expandedIds.size === assignments.length}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    data-testid="ics204-expand-all-btn"
                  >
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Expand All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={collapseAll}
                    disabled={expandedIds.size === 0}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    data-testid="ics204-collapse-all-btn"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    Collapse All
                  </Button>
                </div>
              </div>

              {assignments.map((assignment) => {
                const isExpanded = expandedIds.has(assignment.id);
                return (
                <Card
                  key={assignment.id}
                  className="bg-gray-800/50 border-gray-700 hover:border-orange-500/50 transition-colors"
                  data-testid={`ics204-assignment-card-${assignment.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3 gap-2">
                      <button
                        type="button"
                        onClick={() => toggleExpand(assignment.id)}
                        className="flex items-start gap-2 flex-1 text-left hover:opacity-90"
                        data-testid={`ics204-toggle-expand-${assignment.id}`}
                        aria-expanded={isExpanded}
                      >
                        <span className="mt-0.5 text-gray-400">
                          {isExpanded
                            ? <ChevronDown className="w-5 h-5" />
                            : <ChevronRight className="w-5 h-5" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">
                            {assignment.branch || assignment.division || assignment.group || 'Unnamed Assignment'}
                          </h3>
                          <p className="text-sm text-gray-400">
                            Operational Period: {assignment.operationalPeriod || 'Not specified'}
                          </p>
                        </div>
                      </button>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          onClick={() => handleEdit(assignment)}
                          size="sm"
                          variant="ghost"
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(assignment.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Resources:</span>
                        <span className="text-white ml-2">{assignment.resources?.length || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Supervisor:</span>
                        <span className="text-white ml-2 truncate inline-block max-w-[12rem] align-bottom">
                          {assignment.operationsPersonnel?.divisionGroupSupervisor || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Staging:</span>
                        <span className="text-white ml-2 truncate inline-block max-w-[12rem] align-bottom">
                          {assignment.stagingArea || '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Prepared By:</span>
                        <span className="text-white ml-2 truncate inline-block max-w-[12rem] align-bottom">
                          {assignment.preparedBy || '-'}
                        </span>
                      </div>
                    </div>
                    {!isExpanded && assignment.workAssignments && (
                      <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                        {assignment.workAssignments}
                      </p>
                    )}

                    {isExpanded && (
                      <div
                        className="mt-4 pt-4 border-t border-gray-700 space-y-4 text-sm"
                        data-testid={`ics204-assignment-detail-${assignment.id}`}
                      >
                        {/* Basic info */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div><span className="text-gray-500">Branch:</span> <span className="text-white ml-1">{assignment.branch || '-'}</span></div>
                          <div><span className="text-gray-500">Division:</span> <span className="text-white ml-1">{assignment.division || '-'}</span></div>
                          <div><span className="text-gray-500">Group:</span> <span className="text-white ml-1">{assignment.group || '-'}</span></div>
                          <div><span className="text-gray-500">Period Start:</span> <span className="text-white ml-1">{assignment.operationalPeriodStart || '-'}</span></div>
                          <div><span className="text-gray-500">Period End:</span> <span className="text-white ml-1">{assignment.operationalPeriodEnd || '-'}</span></div>
                          <div><span className="text-gray-500">Date/Time Prepared:</span> <span className="text-white ml-1">{assignment.dateTimePrepared || '-'}</span></div>
                        </div>

                        {/* Operations Personnel */}
                        <div>
                          <h4 className="text-orange-400 font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" /> Operations Personnel
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 pl-5">
                            <div><span className="text-gray-500">Operations Section Chief:</span> <span className="text-white ml-1">{assignment.operationsPersonnel?.operationsSectionChief || '-'}</span></div>
                            <div><span className="text-gray-500">Phone:</span> <span className="text-white ml-1">{assignment.operationsPersonnel?.operationsSectionChiefPhone || '-'}</span></div>
                            <div><span className="text-gray-500">Branch Director:</span> <span className="text-white ml-1">{assignment.operationsPersonnel?.branchDirector || '-'}</span></div>
                            <div><span className="text-gray-500">Phone:</span> <span className="text-white ml-1">{assignment.operationsPersonnel?.branchDirectorPhone || '-'}</span></div>
                            <div><span className="text-gray-500">Division/Group Supervisor:</span> <span className="text-white ml-1">{assignment.operationsPersonnel?.divisionGroupSupervisor || '-'}</span></div>
                            <div><span className="text-gray-500">Phone:</span> <span className="text-white ml-1">{assignment.operationsPersonnel?.divisionGroupSupervisorPhone || '-'}</span></div>
                          </div>
                        </div>

                        {/* Resources */}
                        {assignment.resources && assignment.resources.length > 0 && (
                          <div>
                            <h4 className="text-orange-400 font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                              <Package className="w-3.5 h-3.5" /> Resources ({assignment.resources.length})
                            </h4>
                            <div className="overflow-x-auto pl-5">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500 border-b border-gray-700">
                                    <th className="text-left py-1 pr-3 font-normal w-12">Photo</th>
                                    <th className="text-left py-1 pr-3 font-normal">Resource Identifier</th>
                                    <th className="text-left py-1 pr-3 font-normal">Leader/Contact</th>
                                    <th className="text-left py-1 pr-3 font-normal">Contact Info</th>
                                    <th className="text-left py-1 pr-3 font-normal"># of Persons</th>
                                    <th className="text-left py-1 pr-3 font-normal">Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {assignment.resources.map((r, i) => (
                                    <tr key={i} className="border-b border-gray-800/50">
                                      <td className="py-1 pr-3">
                                        {r.resource_image ? (
                                          <img
                                            src={r.resource_image.startsWith('http') ? r.resource_image : `${API_URL}${r.resource_image}`}
                                            alt={`Resource ${i + 1}`}
                                            className="h-10 w-10 rounded border border-gray-700 object-cover"
                                          />
                                        ) : (
                                          <span className="text-gray-600">—</span>
                                        )}
                                      </td>
                                      <td className="py-1 pr-3 text-white">{r.resourceIdentifier || '-'}</td>
                                      <td className="py-1 pr-3 text-white">{r.leader || '-'}</td>
                                      <td className="py-1 pr-3 text-white">{r.contactInfo || '-'}</td>
                                      <td className="py-1 pr-3 text-white">{r.numberOfPersons || '-'}</td>
                                      <td className="py-1 pr-3 text-white">{r.notes || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Work Assignments */}
                        {assignment.workAssignments && (
                          <div>
                            <h4 className="text-orange-400 font-semibold text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5">
                              <ClipboardList className="w-3.5 h-3.5" /> Work Assignments
                            </h4>
                            <p className="text-white whitespace-pre-wrap pl-5">{assignment.workAssignments}</p>
                          </div>
                        )}

                        {/* Special Instructions */}
                        {assignment.specialInstructions && (
                          <div>
                            <h4 className="text-orange-400 font-semibold text-xs uppercase tracking-wider mb-1 flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5" /> Special Instructions
                            </h4>
                            <p className="text-white whitespace-pre-wrap pl-5">{assignment.specialInstructions}</p>
                          </div>
                        )}

                        {/* Communications */}
                        <div>
                          <h4 className="text-orange-400 font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
                            <Radio className="w-3.5 h-3.5" /> Communications
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-1 pl-5">
                            <div><span className="text-gray-500">Radio Freq:</span> <span className="text-white ml-1">{assignment.communications?.radioFrequency || '-'}</span></div>
                            <div><span className="text-gray-500">Channel:</span> <span className="text-white ml-1">{assignment.communications?.channelName || '-'}</span></div>
                            <div><span className="text-gray-500">Phone:</span> <span className="text-white ml-1">{assignment.communications?.phone || '-'}</span></div>
                            <div><span className="text-gray-500">Med Emergency Ch:</span> <span className="text-white ml-1">{assignment.communications?.medicalEmergencyChannel || '-'}</span></div>
                          </div>
                          {(assignment.communications?.additionalEntries || []).map((entry, idx) => (
                            <div key={idx} className="mt-2 pl-5">
                              <div className="text-xs text-gray-500 mb-1">Additional Entry {idx + 1}</div>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-1">
                                <div><span className="text-gray-500">Radio Freq:</span> <span className="text-white ml-1">{entry.radioFrequency || '-'}</span></div>
                                <div><span className="text-gray-500">Channel:</span> <span className="text-white ml-1">{entry.channelName || '-'}</span></div>
                                <div><span className="text-gray-500">Phone:</span> <span className="text-white ml-1">{entry.phone || '-'}</span></div>
                                <div><span className="text-gray-500">Med Emergency Ch:</span> <span className="text-white ml-1">{entry.medicalEmergencyChannel || '-'}</span></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Quick Assign Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-80 bg-gray-900 border-l border-gray-700 shadow-2xl z-40 transition-transform duration-300 ${
          sidebarOpen && showAddForm ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="ics204-quick-assign-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-gray-700 bg-gray-900/95">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-blue-500" />
                Quick Assign
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                onClick={() => setSidebarOpen(false)}
                data-testid="ics204-close-quick-assign-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-gray-500 text-xs mb-3">
              Drag a <span className="text-teal-300">Resource Unit</span> onto the section to add a row,
              or a <span className="text-blue-300">Person</span> onto a row to set its Leader.
            </p>

            <div className="grid grid-cols-2 gap-1 bg-gray-800/60 p-1 rounded-md mb-3">
              <button
                type="button"
                onClick={() => setSidebarTab('resources')}
                className={`flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded transition-colors ${
                  sidebarTab === 'resources'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="ics204-tab-resources"
              >
                <Package className="h-3.5 w-3.5" />
                Resource Units ({unitsList.length})
              </button>
              <button
                type="button"
                onClick={() => setSidebarTab('personnel')}
                className={`flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded transition-colors ${
                  sidebarTab === 'personnel'
                    ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
                data-testid="ics204-tab-personnel"
              >
                <Users className="h-3.5 w-3.5" />
                Personnel ({participants.length})
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={sidebarTab === 'resources' ? 'Search resource units...' : 'Search personnel...'}
                className="pl-8 bg-gray-800 border-gray-600 text-white text-sm h-8"
                data-testid="ics204-quick-assign-search"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {sidebarTab === 'resources' ? (
              filteredUnits.length === 0 ? (
                <div className="text-center py-6">
                  <Package className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500 text-xs">{unitsList.length === 0 ? 'No resource units in this exercise' : 'No matches'}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredUnits.map(r => (
                    <div
                      key={r.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, r, 'resource')}
                      className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-gray-800/50 border border-gray-700/50 cursor-grab active:cursor-grabbing hover:border-teal-500/30 hover:bg-gray-800 transition-colors group"
                      data-testid={`ics204-draggable-resource-${r.id}`}
                    >
                      <GripVertical className="h-3.5 w-3.5 text-gray-600 group-hover:text-teal-500/60 flex-shrink-0" />
                      <Package className="h-3.5 w-3.5 text-teal-500/40 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{r.unit_name || 'Unnamed Unit'}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {r.resource_type || ''}{r.status ? ` • ${r.status}` : ''}{r.personnel_needed ? ` • ${r.personnel_needed} pax` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              filteredParticipants.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="h-8 w-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500 text-xs">{participants.length === 0 ? 'No personnel in this exercise' : 'No matches'}</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredParticipants.map(p => (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, p, 'personnel')}
                      className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-gray-800/50 border border-gray-700/50 cursor-grab active:cursor-grabbing hover:border-blue-500/30 hover:bg-gray-800 transition-colors group"
                      data-testid={`ics204-draggable-personnel-${p.id}`}
                    >
                      <GripVertical className="h-3.5 w-3.5 text-gray-600 group-hover:text-blue-500/60 flex-shrink-0" />
                      <User className="h-3.5 w-3.5 text-blue-500/40 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{getParticipantName(p)}</p>
                        {(p.position || p.role) && <p className="text-xs text-gray-500 truncate">{p.position || p.role}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          <div className="p-3 border-t border-gray-700 bg-gray-900/95">
            <div className="flex justify-between text-xs text-gray-500">
              <span>{unitsList.length} units · {participants.length} personnel</span>
              <span>{formData.resources.length} row{formData.resources.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  // Weather Module Component

export default ICS204AssignmentList;
